import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon, PencilIcon, TrashIcon,
  ShoppingBagIcon, ShoppingCartIcon, TruckIcon, PhoneIcon, FilmIcon, WifiIcon, GiftIcon
} from '@heroicons/react/24/outline';
import { formatFullDate } from '../utils/dateUtils';

const categoryIcons = {
  food: ShoppingBagIcon, transport: TruckIcon, groceries: ShoppingCartIcon,
  entertainment: FilmIcon, internet: WifiIcon, other: GiftIcon
};
const categoryLabels = {
  food: 'Makan', transport: 'Transport', groceries: 'Belanja',
  entertainment: 'Hiburan', internet: 'Internet', other: 'Lainnya'
};

const TransactionDetailModal = ({ transaction, onClose, onEdit, onDelete }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const Icon = categoryIcons[transaction.category] || GiftIcon;

  const fmt = (n) => 'Rp ' + n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });



  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-[60] flex items-end sm:items-center justify-center" onClick={onClose}>
        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white rounded-t-[32px] sm:rounded-3xl p-6 w-full max-w-md shadow-[0_-10px_40px_rgba(0,0,0,0.1)]"
          onClick={(e) => e.stopPropagation()}>

          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-6 sm:hidden"></div>

          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Detail Transaksi</h3>
            <button onClick={onClose} data-testid="close-transaction-detail"
              className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors">
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Transaction info */}
          <div className="bg-surface p-5 rounded-3xl mb-5">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-slate-900">{transaction.description}</h4>
                <span className="inline-block text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full mt-1">
                  {categoryLabels[transaction.category] || 'Lainnya'}
                </span>
              </div>
            </div>

            <div className="text-center py-3 border-t border-border">
              <p className="text-3xl font-extrabold text-slate-900">{fmt(transaction.amount)}</p>
            </div>

            <div className="space-y-3 mt-4">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Dibayar oleh</span>
                <span className="font-semibold text-primary">{transaction.paidBy}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Dibagi ke</span>
                <span className="font-semibold text-slate-900">{transaction.splitBetween.join(', ')}</span>
              </div>
              {transaction.splitBetween.length > 0 && (
                (() => {
                  const isCustom = transaction.splitDetails && transaction.splitDetails.some(detail => {
                    const expected = transaction.amount / transaction.splitBetween.length;
                    return Math.abs(detail.amount - expected) > 1;
                  });

                  if (isCustom) {
                    return (
                      <div className="mt-4 pt-4 border-t border-border border-dashed">
                        <span className="text-slate-500 text-xs mb-2 block font-semibold uppercase tracking-wider">Detail Bagi</span>
                        <div className="space-y-2">
                          {transaction.splitDetails.map((detail, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-slate-700">{detail.name}</span>
                              <span className="font-semibold text-slate-900">{fmt(detail.amount)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Per orang</span>
                      <span className="font-semibold text-slate-900">
                        {fmt(Math.round(transaction.amount / transaction.splitBetween.length))}
                      </span>
                    </div>
                  );
                })()
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Tanggal</span>
                <span className="text-slate-700">{formatFullDate(transaction.date)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button onClick={onEdit} data-testid="edit-transaction-btn"
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all">
              <PencilIcon className="w-4 h-4" />
              Edit
            </button>

            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} data-testid="delete-transaction-btn"
                className="flex items-center justify-center gap-2 px-5 py-3 bg-danger-bg text-danger rounded-full font-semibold hover:bg-danger/10 active:scale-95 transition-all">
                <TrashIcon className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => { onDelete(transaction.id); onClose(); }} data-testid="confirm-delete-btn"
                className="flex items-center justify-center gap-2 px-5 py-3 bg-danger text-white rounded-full font-semibold hover:bg-danger/90 active:scale-95 transition-all animate-pulse">
                <TrashIcon className="w-4 h-4" />
                Hapus?
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TransactionDetailModal;
